import React, {
  useRef, useLayoutEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import IconButton from '@digital-hig/mui/dist/IconButton';
import { window, document } from 'global';
// eslint-disable-next-line import/no-extraneous-dependencies
import SVGIcon from '@mui/material/SvgIcon';
import SVGCaretLeft from '@digital-hig/icon/lib/esm/icons/caret-left';
import SVGCaretRight from '@digital-hig/icon/lib/esm/icons/caret-right';
import logger from '../common/logger';
import { styleVariables } from '../common/constants';

const defaultOptions = {
  scrollBy: 'item',
  mobileCount: 1,
  mobileBreakpoint: 800,
  tabletCount: 3,
  tabletBreakpoint: 1040,
  desktopCount: 5,
};

const cardMarginRight = 16;

const Carousel = ({
  children, options, title, scrollEventCallback, isAlignedRight,
}) => {
  const wrapperRef = useRef();
  const trayRef = useRef();
  const thisRef = useRef();
  const [isLeftDisabled, setLeftDisabled] = useState(true);
  const [isRightDisabled, setRightDisabled] = useState(false);

  let scrollLeft;
  let scrollRight;
  const [isMobile, setIsMobile] = useState(false);

  const mergedOptions = { ...defaultOptions, ...options };

  /* Slide one item out of view */
  const getItemScrollDistance = () => {
    const firstChild = trayRef.current.querySelector('*');
    const itemWidth = firstChild?.offsetWidth + 8;
    return itemWidth;
  };

  const updateDisabledState = () => {
    setTimeout(() => {
      if (wrapperRef?.current) {
        const itemWidth = getItemScrollDistance(); // Get the width of a single item
        const leftState = wrapperRef.current.scrollLeft < 100; // comparing to 0 isn't exactly working as there is a small scroll margin still visible
        const rightState = wrapperRef.current.scrollLeft + wrapperRef.current.offsetWidth >= wrapperRef.current.scrollWidth - 1; // Adjust for precision

        // Calculate the number of items visible in the carousel
        const itemsVisible = Math.floor(wrapperRef.current.offsetWidth / itemWidth);
        // Calculate the number of items scrolled
        const itemsScrolled = Math.round(wrapperRef.current.scrollLeft / itemWidth);

        // Determine if there are items left to scroll to
        const isItemsLeft = itemsScrolled + itemsVisible < children?.length;

        if (isLeftDisabled !== leftState) {
          setLeftDisabled(leftState);
        }
        if (isRightDisabled !== (rightState || !isItemsLeft)) {
          setRightDisabled(rightState || !isItemsLeft);
        }
      }
    }, 500);
  };

  const getItemCount = () => {
    if (window.innerWidth <= mergedOptions.mobileBreakpoint) { // Mobile
      return mergedOptions.mobileCount;
    } if (window.innerWidth <= mergedOptions.tabletBreakpoint) { // Tablet
      return mergedOptions.tabletCount;
    }
    return mergedOptions.desktopCount;
  };

  const resize = () => {
    if (isAlignedRight) {
      try {
        const compStyle = window.getComputedStyle(thisRef.current.parentNode.parentNode);
        const newWidth = `${document.body.clientWidth - parseFloat(compStyle.getPropertyValue('margin-left'), 10) - parseFloat(compStyle.getPropertyValue('padding-left'), 10)}px`;
        wrapperRef.current.style.maxWidth = newWidth;
        wrapperRef.current.style.width = newWidth;
      } catch (e) {
        logger.info('Failed setting alignment  ', e);
      }
    }
    const itemCount = getItemCount();
    const scaler = itemCount > 1 ? 0.95 : 0.9;
    if (window.innerWidth <= 560) { // Mobile
      if (!isMobile) {
        setIsMobile(true);
      }
    } else if (isMobile) { // Desktop
      setIsMobile(false);
    }

    Array.from(trayRef.current.children).forEach((child) => {
      if (trayRef.current.children.length > itemCount) {
        // eslint-disable-next-line no-param-reassign
        child.style.minWidth = `${Math.floor((wrapperRef.current.offsetWidth / itemCount) - ((8 * (itemCount - 1)) / itemCount) - (8 / itemCount)) * (scaler)}px`;
      } else {
        const compStyle = getComputedStyle(thisRef.current.parentNode);
        const parentTrueWidth = thisRef.current.parentNode.clientWidth - parseFloat(compStyle.paddingLeft) - parseFloat(compStyle.paddingRight);
        // eslint-disable-next-line no-param-reassign
        child.style.width = `${Math.floor((parentTrueWidth / itemCount) - ((8 * (itemCount - 1)) / itemCount) - (8 / itemCount)) * (scaler)}px`;
      }
    });
    updateDisabledState();
  };

  useLayoutEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  });

  const scrollItemRight = () => {
    wrapperRef?.current?.scrollBy({ left: getItemScrollDistance(), top: 0, behavior: 'smooth' });
    setRightDisabled(true);
    updateDisabledState();
    scrollEventCallback('Next');
  };

  const scrollItemLeft = () => {
    wrapperRef?.current?.scrollBy({ left: -getItemScrollDistance(), top: 0, behavior: 'smooth' });
    setLeftDisabled(true);
    updateDisabledState();
    scrollEventCallback('Previous');
  };

  /* Slide all current items out of view */
  const getTrayScrollDistance = () => {
    const firstChild = trayRef.current.querySelector('*');
    const itemWidth = (parseFloat(firstChild.style.minWidth, 10) + cardMarginRight) * getItemCount();
    return itemWidth;
  };

  const scrollTrayRight = () => {
    wrapperRef.current?.scrollBy({ left: getTrayScrollDistance(), top: 0, behavior: 'smooth' });
    updateDisabledState();
    scrollEventCallback('Next');
  };

  const scrollTrayLeft = () => {
    wrapperRef.current?.scrollBy({ left: -getTrayScrollDistance(), top: 0, behavior: 'smooth' });
    updateDisabledState();
    scrollEventCallback('Previous');
  };

  switch (mergedOptions.scrollBy) {
  case 'tray':
    scrollRight = scrollTrayRight;
    scrollLeft = scrollTrayLeft;
    break;

  default:
    scrollRight = scrollItemRight;
    scrollLeft = scrollItemLeft;
  }

  // window.wrapperRef = wrapperRef;
  return (
    <RootElement ref={thisRef}>
      <div className="carouselHeader">
        <div className="carouselTitle">
          {title}
        </div>
        {!isMobile && children?.length > 0 && !(isLeftDisabled && isRightDisabled) && (
          <div className="controls">
            <IconButton onClick={scrollLeft} variant="outlined" disabled={isLeftDisabled}><SVGIcon size="medium"><SVGCaretLeft /></SVGIcon></IconButton>
            <IconButton onClick={scrollRight} variant="outlined" disabled={isRightDisabled}><SVGIcon size="medium"><SVGCaretRight /></SVGIcon></IconButton>
          </div>
        )}
      </div>
      <div className="scrollWrapper" ref={wrapperRef}>
        <div className="scrollTray" ref={trayRef}>{children}</div>
      </div>
    </RootElement>
  );
};

export const scrollWrapperCss = `
  .scrollWrapper {
    width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    
    
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
      &::-webkit-scrollbar {
        display: none;
      }
    
    

    .scrollTray {
      width: fit-content;
      display: flex;
      margin-right: 24px;

      & > *:not(style) {
        margin-right: ${cardMarginRight}px;
        display: flex;
      }
      
      & > *:last-child {
        margin-right: 0px;
      }
     

    }
  }
`;

const RootElement = styled.div`
  width: 100%;
  
  .carouselHeader {
    display: flex;
    margin-bottom: 28px;

    p {
      font-size: 16px;
    }


  }
  
  .carouselTitle {
    flex: 1;
    
   
    
   > :first-child:not(style) {
      max-width: 860px;
      padding-right: 48px;
      display: block;

     > :last-child {
       margin-bottom: 0px;
       padding-bottom: 0px;
       margin-top: 0px;
     }
   }
  }
  .controls {
    display: flex;
    align-self: flex-end;
    margin-bottom: 2px;
    
    & > button {
      flex: 1;
      border-radius: 50%; 
      width:36px;
      height:36px;
      border: 1px solid ${styleVariables.divider};
      &:disabled {
        border: 1px solid ${styleVariables.lightDivider};
      }
     
      &:first-child {
        margin-right: 8px;
      }
    }
  }

  ${scrollWrapperCss}
`;

Carousel.propTypes = {
  children: PropTypes.arrayOf(PropTypes.shape({})),
  options: PropTypes.shape({ scrollBy: PropTypes.string }),
  title: PropTypes.shape({}),
  scrollEventCallback: PropTypes.func,
  isAlignedRight: PropTypes.bool,
};

Carousel.defaultProps = {
  children: null,
  options: defaultOptions,
  title: <></>,
  scrollEventCallback: () => {},
  isAlignedRight: false,
};

export default Carousel;
